import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Cell, DeprecatedIcon, Loader, Modal, Spacer, Text } from '@vinted/web-ui';
import { useIntl } from 'react-intl';
import FaqEntryUrl from 'components/FaqEntryUrl';
import useAbTest from 'hooks/useAbTest';
import useTranslate from 'hooks/useTranslate';
import useTracking from 'hooks/useTracking/useTracking';
import { getLocale } from 'state/intl/selectors';
import { clickEvent } from 'libs/common/event-tracker/events';
import { formatCurrencyAmount } from 'libs/utils/formatString';
import { AccessChannel } from 'constants/index';
import { FaqEntryType } from 'constants/faq-entry';
import { Screen } from 'constants/tracking/screens';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { ExtraServiceOrderStatus, VAS_PENDING_STATE_AB_TEST_EXTENDED_PAYMENT_POLL_DURATION } from 'constants/extra-service';
import usePendingPaymentCountdown from './usePendingPaymentCountdown';
import useTrackViewExtraServiceCheckout from '../hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ACTIONABLE_STATUSES = [ExtraServiceOrderStatus.Failure, ExtraServiceOrderStatus.Success];

const ExtraServiceOrderStatusModal = _ref => {
  var _useAbTest;

  let {
    show,
    orderId,
    orderType,
    orderStatus,
    errorMessage,
    orderPayable,
    onContinue
  } = _ref;
  const intl = useIntl();
  const {
    track
  } = useTracking();
  const locale = useSelector(getLocale);
  const translate = useTranslate("extra_services.checkout.order_status_modal.".concat(orderType));
  const translateCommon = useTranslate('extra_services.checkout.order_status_modal.common');
  const VASPendingStateAbTestVariant = ((_useAbTest = useAbTest({
    shouldTrackExpose: show,
    abTestName: 'vas_pending_state'
  })) === null || _useAbTest === void 0 ? void 0 : _useAbTest.variant) || 'off';
  const isVASPendingStateAbTestEnabled = VASPendingStateAbTestVariant !== 'off';
  const countdownDuration = VAS_PENDING_STATE_AB_TEST_EXTENDED_PAYMENT_POLL_DURATION[VASPendingStateAbTestVariant];
  const countdown = usePendingPaymentCountdown({
    start: show && isVASPendingStateAbTestEnabled && orderStatus === ExtraServiceOrderStatus.Processing,
    duration: countdownDuration
  });
  useTrackViewExtraServiceCheckout({
    orderId,
    orderType,
    screen: Screen.ExtraServicePendingPaymentModal,
    shouldTrack: show && countdown.state === 'inProgress'
  }); // TODO: If vas_pending_state ab test is scaled, move this logic to a separate components for each payment state.

  const commonOverridenContent = useMemo(() => {
    if (!isVASPendingStateAbTestEnabled) return null;

    if (orderStatus === ExtraServiceOrderStatus.Processing && countdown.state !== 'idle') {
      const formattedCountdownDuration = intl.formatDate(new Date(countdownDuration), {
        second: '2-digit'
      });
      return {
        title: translateCommon('long_processing.title'),
        action: translateCommon('long_processing.action'),
        explanation: countdown.state === 'inProgress' ? translateCommon('long_processing.explanation.countdown', {
          countdownDuration: formattedCountdownDuration
        }) : translateCommon('long_processing.explanation.continue_waiting')
      };
    }

    if (orderStatus === ExtraServiceOrderStatus.Failure) {
      const values = {
        'help-center': chunks => /*#__PURE__*/_jsx(FaqEntryUrl, {
          type: FaqEntryType.PaymentFailed,
          accessChannel: AccessChannel.ProductLink,
          render: url => /*#__PURE__*/_jsx("a", {
            href: url,
            target: "_blank",
            rel: "noreferrer",
            "data-testid": "failed-order-explanation-faq-link",
            children: chunks
          })
        })
      };
      return {
        title: translateCommon("".concat(orderStatus, ".title")),
        explanation: translateCommon("".concat(orderStatus, ".explanation"), values),
        action: translateCommon("".concat(orderStatus, ".action"))
      };
    }

    return null;
  }, [intl, orderStatus, countdown.state, translateCommon, countdownDuration, isVASPendingStateAbTestEnabled]);

  function handleOnContinue() {
    if (isVASPendingStateAbTestEnabled && countdown.state !== 'inProgress') {
      track(clickEvent({
        screen: Screen.ExtraServicePendingPaymentModal,
        target: ClickableElement.ExtraServicePendingPaymentTryAgain,
        targetDetails: JSON.stringify({
          type: orderType,
          service_order_id: orderId
        })
      }));
    }

    onContinue();
  }

  function renderBottomPollingNote() {
    if (countdown.state === 'idle') return null;
    if (orderStatus !== ExtraServiceOrderStatus.Processing) return null;
    const formattedCountdown = countdown.state === 'inProgress' ? intl.formatDate(new Date(countdown.remainingTime), {
      minute: '2-digit',
      second: '2-digit'
    }) : null;
    const content = countdown.state === 'inProgress' ? translateCommon('long_processing.bottom_note.countdown', {
      countdown: formattedCountdown
    }) : translateCommon('long_processing.bottom_note.continue_waiting');
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsx(Text, {
        text: content,
        type: Text.Type.Caption,
        width: Text.Width.Parent,
        alignment: Text.Alignment.Center
      })]
    });
  }

  function renderActions() {
    const isLongPending = isVASPendingStateAbTestEnabled && orderStatus === ExtraServiceOrderStatus.Processing && countdown.state !== 'idle';
    const isActionable = ACTIONABLE_STATUSES.includes(orderStatus);
    if (!isActionable && !isLongPending) return null;
    return /*#__PURE__*/_jsx(Button, {
      disabled: isLongPending && countdown.state !== 'over',
      text: (commonOverridenContent === null || commonOverridenContent === void 0 ? void 0 : commonOverridenContent.action) || translate("".concat(orderStatus, ".action")),
      styling: Button.Styling.Filled,
      onClick: handleOnContinue,
      testId: "continue-button"
    });
  }

  function renderIcon() {
    switch (orderStatus) {
      case ExtraServiceOrderStatus.Success:
        return /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "check-circle-filled",
          testId: "extra-service-order-success-icon",
          color: DeprecatedIcon.Color.Success,
          size: DeprecatedIcon.Size.Large
        });

      case ExtraServiceOrderStatus.Failure:
        return /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "cancel-circle-filled",
          testId: "extra-service-order-failure-icon",
          color: DeprecatedIcon.Color.Warning,
          size: DeprecatedIcon.Size.Large
        });

      case ExtraServiceOrderStatus.Processing:
      default:
        return /*#__PURE__*/_jsx(Loader, {
          testId: "extra-service-order-confirmation-loader",
          size: Loader.Size.XLarge
        });
    }
  }

  if (orderStatus === ExtraServiceOrderStatus.Idle) return null;
  const formattedOrderPayable = orderPayable && formatCurrencyAmount(orderPayable, locale);
  return /*#__PURE__*/_jsx(Modal, {
    show: show,
    testId: "extra-service-order-status-modal",
    children: /*#__PURE__*/_jsxs(Cell, {
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsx("div", {
        className: "u-flexbox u-justify-content-center",
        children: renderIcon()
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.X2Large
      }), /*#__PURE__*/_jsx(Text, {
        text: (commonOverridenContent === null || commonOverridenContent === void 0 ? void 0 : commonOverridenContent.title) || translate("".concat(orderStatus, ".title"), {
          orderPayable: formattedOrderPayable
        }),
        alignment: Text.Alignment.Center,
        width: Text.Width.Parent,
        type: Text.Type.Heading
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsx(Text, {
        text: errorMessage || (commonOverridenContent === null || commonOverridenContent === void 0 ? void 0 : commonOverridenContent.explanation) || translate("".concat(orderStatus, ".explanation")),
        alignment: Text.Alignment.Center,
        width: Text.Width.Parent
      }), /*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.X2Large
      }), renderActions(), renderBottomPollingNote()]
    })
  });
};

export default ExtraServiceOrderStatusModal;